/**
 * Exports de l'historique : CSV (Excel français) et GPX 1.1. Logique PURE (chaînes en sortie) ; le téléchargement est dans l'écran.
 *
 * CSV : BOM UTF-8, séparateur « ; », fin de ligne CRLF, décimale « , » (Excel français lit « 12.5 » comme une date ou du texte), dates en
 *       heure de Bamako. Une cellule TEXTE qui commence par = + - @ (ou tabulation / retour chariot) est NEUTRALISÉE par une apostrophe :
 *       un nom de lieu ou de boîtier saisi par un tiers ne doit jamais devenir une formule (injection CSV).
 * GPX : tout texte est échappé XML et débarrassé des caractères de contrôle interdits en XML 1.0.
 */
import { formatDateHeure, formatDuree } from './periodes'
import type { Arret, Position, Resume, Trajet } from './types'

export type Cellule = string | number | boolean | null | undefined

export const BOM = '﻿'

export function cellule(v: Cellule): string {
  if (v == null) return ''
  if (typeof v === 'boolean') return v ? 'oui' : 'non'
  if (typeof v === 'number') return Number.isFinite(v) ? String(v).replace('.', ',') : ''
  let s = v
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`
  return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export function versCsv(lignes: readonly (readonly Cellule[])[]): string {
  return BOM + lignes.map((l) => l.map(cellule).join(';')).join('\r\n') + '\r\n'
}

const km = (m: number) => Math.round(m / 10) / 100 // kilomètres, 2 décimales
const minutes = (s: number) => Math.round(s / 6) / 10 // minutes, 1 décimale

export function csvTrajets(trajets: readonly Trajet[], vehicule: string): string {
  return versCsv([
    ['Véhicule', 'N°', 'Départ', 'Arrivée', 'Durée', 'Durée (min)', 'Distance (km)', 'Vitesse moyenne (km/h)', 'Vitesse maximale (km/h)', 'Lieu de départ', 'Lieu d\'arrivée', 'Latitude départ', 'Longitude départ', 'Latitude arrivée', 'Longitude arrivée'],
    ...trajets.map((t) => [
      vehicule, t.n, formatDateHeure(t.debut * 1000), formatDateHeure(t.fin * 1000), formatDuree(t.dureeS), minutes(t.dureeS), km(t.distanceM),
      t.vitesseMoyKmh, t.vitesseMaxKmh, t.lieuDebut, t.lieuFin, t.latDebut, t.lngDebut, t.latFin, t.lngFin,
    ]),
  ])
}

export function csvArrets(arrets: readonly Arret[], vehicule: string): string {
  return versCsv([
    ['Véhicule', 'N°', 'Début', 'Fin', 'Durée', 'Durée (min)', 'Lieu', 'Moteur allumé', 'Moteur allumé (min)', 'Latitude', 'Longitude'],
    ...arrets.map((a) => [
      vehicule, a.n, formatDateHeure(a.debut * 1000), formatDateHeure(a.fin * 1000), formatDuree(a.dureeS), minutes(a.dureeS), a.lieu,
      a.moteurAllume === null ? 'inconnu' : a.moteurAllume, minutes(a.moteurAllumeS), a.lat, a.lng,
    ]),
  ])
}

export function csvSynthese(lignes: readonly { vehicule: string; resume: Resume }[]): string {
  return versCsv([
    ['Véhicule', 'Période du', 'Période au', 'Distance (km)', 'Temps de conduite (min)', 'Temps à l\'arrêt (min)', 'dont moteur allumé (min)', 'Sans signal (min)',
      'Vitesse maximale (km/h)', 'Vitesse moyenne (km/h)', 'Trajets', 'Arrêts', 'Alertes'],
    ...lignes.map(({ vehicule, resume: r }) => [
      vehicule, formatDateHeure(r.du * 1000), formatDateHeure(r.au * 1000), km(r.distanceM), minutes(r.conduiteS), minutes(r.arretS), minutes(r.arretMoteurAllumeS),
      minutes(r.sansSignalS), r.vitesseMaxKmh, r.vitesseMoyKmh, r.nbTrajets, r.nbArrets, r.nbAlertes === null ? 'non disponible' : r.nbAlertes,
    ]),
  ])
}

/* ───────────────────────────────── GPX ───────────────────────────────── */

/** Échappement XML + suppression des caractères de contrôle (interdits en XML 1.0, sauf tab / LF / CR). */
export function echapperXml(s: string): string {
  return s
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

const iso = (s: number) => new Date(Math.round(s * 1000)).toISOString().replace(/\.\d{3}Z$/, 'Z')

export interface PisteGpx {
  nom: string
  points: readonly Position[]
}

/**
 * GPX 1.1 : une `<trk>` par véhicule (un `<trkseg>` par morceau : une absence de signal de plus de `coupureS` coupe la ligne),
 * plus un `<wpt>` par arrêt. Lisible par Google Earth, Garmin, QGIS, OsmAnd.
 */
export function versGpx(pistes: readonly PisteGpx[], arrets: readonly { vehicule: string; arret: Arret }[] = [], coupureS = 1800): string {
  const sortie: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.1" creator="SwiftDeliver" xmlns="http://www.topografix.com/GPX/1/1">',
    '  <metadata><name>Historique des trajets</name></metadata>',
  ]
  for (const { vehicule, arret: a } of arrets) {
    sortie.push(
      `  <wpt lat="${a.lat.toFixed(6)}" lon="${a.lng.toFixed(6)}"><time>${iso(a.debut)}</time>` +
        `<name>${echapperXml(`Arrêt ${a.n} · ${vehicule}`)}</name><desc>${echapperXml(`${formatDuree(a.dureeS)}${a.lieu ? ` · ${a.lieu}` : ''}`)}</desc></wpt>`,
    )
  }
  for (const piste of pistes) {
    sortie.push('  <trk>', `    <name>${echapperXml(piste.nom)}</name>`)
    let ouvert = false
    let precedent: Position | null = null
    for (const p of piste.points) {
      if (!ouvert || (precedent && p.t - precedent.t > coupureS)) {
        if (ouvert) sortie.push('    </trkseg>')
        sortie.push('    <trkseg>')
        ouvert = true
      }
      sortie.push(`      <trkpt lat="${p.lat.toFixed(6)}" lon="${p.lng.toFixed(6)}"><time>${iso(p.t)}</time></trkpt>`)
      precedent = p
    }
    if (ouvert) sortie.push('    </trkseg>')
    sortie.push('  </trk>')
  }
  sortie.push('</gpx>')
  return sortie.join('\n') + '\n'
}

/** Nom de fichier sûr : lettres, chiffres, tirets ; jamais de séparateur de chemin. */
export function nomFichier(base: string, extension: 'csv' | 'gpx', jour: string): string {
  const propre = base.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'flotte'
  return `${propre}-${jour.replace(/[^0-9-]/g, '')}.${extension}`
}
